/** @jsxImportSource @emotion/react */
import React from "react";
import { Interpolation, Theme } from "@emotion/react";
import { useTranslation } from "react-i18next";
import Colors from "styles/colors";
import { useReportTags } from "pages/reports/bi-dashboard-next/hooks/useReportTags";
import { ReportsListItemButton } from "pages/reports/bi-dashboard-next/components/molecules/Reports.ListItemButton.molecule";
import { DistinctTagDTO } from "pages/reports/bi-dashboard-next/models/DistinctTagDTO";

interface ReportsTagsListProps {
  css?: Interpolation<Theme>;
  onTagClick?: (tag: DistinctTagDTO) => void;
  selectedTags?: string[];
  onClearAllTags?: () => void;
}

export const ReportsTagsList: React.FC<ReportsTagsListProps> = ({
  css: cssProp,
  onTagClick,
  selectedTags = [],
  onClearAllTags,
}) => {
  const { t } = useTranslation("reports");

  const { distinctTags, isLoadingDistinctTags, distinctTagsError } =
    useReportTags();

  if (isLoadingDistinctTags) {
    return (
      <div
        css={[
          { padding: "10px", color: Colors.biDashboardView.LOADING_TAGS_TEXT },
          cssProp,
        ]}
      >
        {t("Loading tags...")}
      </div>
    );
  }

  if (distinctTagsError) {
    return (
      <div
        css={[
          { padding: "10px", color: Colors.biDashboardView.ERROR_TEXT },
          cssProp,
        ]}
      >
        {t("Error loading tags:")}: {(distinctTagsError as Error).message}
      </div>
    );
  }

  if (!distinctTags.length) {
    return (
      <div
        css={[
          { padding: "10px", color: Colors.biDashboardView.LOADING_TAGS_TEXT },
          cssProp,
        ]}
      >
        {t("No tags found")}
      </div>
    );
  }

  return (
    <div data-qa="reports-tags-list-container" css={[cssProp]}>
      <div
        data-qa="reports-tags-list-title"
        css={{
          fontSize: 18,
          fontWeight: 600,
          margin: "0 10px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {t("Tags")}
        {selectedTags.length > 0 && onClearAllTags && (
          <button
            data-qa="clear-all-tags-button"
            onClick={onClearAllTags}
            css={{
              fontSize: 12,
              color: Colors.biDashboardView.CLEAR_ALL_BUTTON_TEXT,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "2px 5px",
              borderRadius: 3,
              "&:hover": {
                color: Colors.biDashboardView.CLEAR_ALL_BUTTON_HOVER_TEXT,
                background:
                  Colors.biDashboardView.CLEAR_ALL_BUTTON_HOVER_BACKGROUND,
              },
            }}
          >
            {t("Clear All")}
          </button>
        )}
      </div>
      {distinctTags.map((tagItem, index) => (
        <ReportsListItemButton
          key={`ReportsListItemButton-Tag-${tagItem.tag}-${index}`}
          data-qa={`tag-list-item-${index}`}
          text={tagItem.tag}
          count={tagItem.count}
          isActive={selectedTags.includes(tagItem.tag)}
          onClick={onTagClick ? () => onTagClick(tagItem) : undefined}
        />
      ))}
    </div>
  );
};

export default ReportsTagsList;
